import React, { useCallback, useMemo } from 'react';
import { AccordionGroup, AccordionItem } from './Accordion';
import { ExportImages, AddToReport, ImageRevert } from '../Buttons';
import EditorSideBarRight from '../../components/editorSideBarRight';

import { FieldImageName } from '../../components/editorSideBarRight/Fields';
import DoneEditing from '../Buttons/DoneEditing';

// function SubHeader({ label = '' }) {
//   return (
//     <Typography
//       sx={{
//         paddingTop: '18px',
//         borderBottom: 'solid 1px gray',
//         color: 'grey.300',
//       }}
//     >
//       {label.toUpperCase()}
//     </Typography>
//   );
// }

function SidebarEditor({
  settings = {},
  activeImages = [],
  firstImageData = {},
  groupImageData = {},
  palettes = [],
  onChange = null,
  onNameChange = null,
}) {
  const { imageId, imageName } = useMemo(() => {
    const { DATA = {}, sort_info = {} } = firstImageData ?? {};

    return { imageId: DATA?.imageId ?? '', imageName: sort_info?.origin_path };
  }, [activeImages, firstImageData]);

  const handleNameChange = useCallback(
    (nextName = '') => {
      onNameChange?.([imageId], nextName);
    },
    [imageId, onNameChange],
  );

  return (
    <>
      <div style={styles.reporting}>
        <div style={styles.column}>
          <div style={styles.DoneRevertButtonRow}>
            <ImageRevert disabled={true} style={styles.button} />
            <DoneEditing groupImageData={groupImageData} style={styles.button} />
          </div>
          <AddToReport groupImageData={groupImageData} />
          <ExportImages groupImageData={groupImageData} />
        </div>
      </div>
      <AccordionGroup>
        <AccordionItem label="Image Settings" expanded={true}>
          <FieldImageName
            value={imageName}
            onChange={handleNameChange}
            disabled={activeImages.length > 1}
          />
          <EditorSideBarRight settings={settings} palettes={palettes} onChange={onChange} />
        </AccordionItem>
      </AccordionGroup>
    </>
  );
}

const styles = {
  row: {
    width: '100%',
    display: 'flex',
    flexDirection: 'row',
    gap: '8px',
  },
  column: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  DoneRevertButtonRow: {
    width: '100%',
    display: 'flex',
    flexDirection: 'row',
    gap: '8px',
    justifyContent: 'space-between',
  },
  button: {
    flex: 1,
    margin: '0 4px',
  },
  reporting: {
    padding: '18px 8px',
  },
};

const arePropsEqual = (prevProps, nextProps) => {
  const areArraysEqual = (array1, array2) => {
    if (array1 == null || array2 == null) {
      return array1 === array2;
    }
    if (array1.length !== array2.length) {
      return false;
    }

    for (let i = 0; i < array1.length; i++) {
      if (array1[i] !== array2[i]) {
        return false;
      }
    }

    return true;
  };

  const prevSettings = prevProps.settings;
  const nextSettings = nextProps.settings;

  const keysToCheck = [
    'paletteScheme',
    'capturedEmissivity',
    'capturedTransmissionFactor',
    'capturedBackgroundTempC',
    'minDisplayableTempC',
    'maxDisplayableTempC',
    'temperatureUnit',
    'status',
  ];

  for (const key of keysToCheck) {
    if (prevSettings[key] !== nextSettings[key]) {
      // Change detected, return false to trigger rerender
      return false;
    }
  }

  const prevImageId = prevProps.firstImageData?.DATA?.imageId;
  const nextImageId = nextProps.firstImageData?.DATA?.imageId;
  const prevImageName = prevProps.firstImageData?.DATA?.sort_info?.origin_path;
  const nextImageName = nextProps.firstImageData?.DATA?.sort_info?.origin_path;

  // groupImageData will most likely always trigger re-render
  return (
    // states
    prevImageId === nextImageId &&
    prevImageName === nextImageName &&
    prevProps.groupImageData === nextProps.groupImageData &&
    areArraysEqual(prevProps.palettes, nextProps.palettes) &&
    areArraysEqual(prevProps.activeImages, nextProps.activeImages) &&
    // functions
    prevProps.onChange === nextProps.onChange &&
    prevProps.onNameChange === nextProps.onNameChange
  );
};

export default React.memo(SidebarEditor, arePropsEqual);
